import { useForm } from "react-hook-form";
import React, { useRef, useContext, useState, useEffect, useMemo } from "react";
import moment from 'moment';
import { bankingService } from "../../services/bankingService";
import { createModal } from "react-modal-promise";
import GeneralModal from "../../helpers/GeneralModal";
import LogicContext from "../../Context/LogicContext";
import GeneralFileUploadPreview from "../../components/GeneralFileUploadPreview";
import { GetManualJournalAtom } from "../../store/manualJounalAtom";


// ─── Icons ────────────────────────────────────────────────────────────────────
const ChevronLeft = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
);
const XIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const inputCls = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition";
const selectCls = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition appearance-none";

const FormField = ({ label, children, required, error }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
        {children}
        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
);


export const CreateTransferJournalPanel = ({ isOpen, onClose, onBack, selectedType, bankName, accountId, onSaved, editData, accountsList = [] }) => {
    const [saving, setSaving] = useState(false);
    const [attachmentFile, setAttachmentFile] = useState(null);
    const [existingAttachmentUrl, setExistingAttachmentUrl] = useState('');
    const [fileKey, setFileKey] = useState(0);
    const [currencyOptions, setCurrencyOptions] = useState([]);
    const isMounted = useRef(false);

    const myPromiseModal = createModal(GeneralModal);
    const logicContext = useContext(LogicContext);
    const journalAtom = GetManualJournalAtom();

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: {
            journal_date: moment().format('YYYY-MM-DD'),
            reference_number: '',
            from_account_id: accountId,
            to_account_id: '',
            notes: '',
            currency_id: '',
            amount: '',
        }
    });

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    useEffect(() => {
        if (journalAtom?.currency_info) setCurrencyOptions(journalAtom.currency_info);
    }, [journalAtom]);

    // Reset form and state when panel opens fresh (not in edit mode) or closes
    useEffect(() => {
        if (isOpen && !editData?.account_ref_no) {
            reset({
                journal_date: moment().format('YYYY-MM-DD'),
                reference_number: '',
                from_account_id: accountId,
                to_account_id: '',
                notes: '',
                currency_id: '',
                amount: '',
            });
            setAttachmentFile(null);
            setExistingAttachmentUrl('');
            setFileKey(prev => prev + 1);
        } else if (!isOpen) {
            reset({
                journal_date: moment().format('YYYY-MM-DD'),
                reference_number: '',
                from_account_id: accountId,
                to_account_id: '',
                notes: '',
                currency_id: '',
                amount: '',
            });
            setAttachmentFile(null);
            setExistingAttachmentUrl('');
            setFileKey(prev => prev + 1);
        }
    }, [isOpen]);

    // Populate form when opening in edit mode
    useEffect(() => {
        if (editData?.account_ref_no && isOpen) {
            reset({
                journal_date: moment(editData.journal_date).format('YYYY-MM-DD'),
                reference_number: String(editData.reference_number || ''),
                from_account_id: editData.from_account_id ? String(editData.from_account_id) : accountId,
                to_account_id: editData.to_account_id ? String(editData.to_account_id) : '',
                notes: editData.notes || '',
                currency_id: editData.currency_id ? String(editData.currency_id) : '',
                amount: editData.amount || '',
            });
            setAttachmentFile(null);
            setExistingAttachmentUrl(editData.attachment_url || '');
            setFileKey(prev => prev + 1);
        }
    }, [editData, isOpen]);

    const groupedAccountOptions = useMemo(() => {
        const groups = {};
        accountsList.forEach(acc => {
            const key = acc.parent_account_id;
            if (!groups[key]) groups[key] = { label: acc.parent_account_name || String(key), accounts: [] };
            groups[key].accounts.push(acc);
        });
        return Object.values(groups);
    }, [accountsList]);

    const onFileSelected = (file) => setAttachmentFile(file);

    const onSubmit = async (data) => {
        const result = await myPromiseModal({
            title: "Transfer to Another Account",
            body: "Confirm transfer to another account?",
            showInput: false,
        });
        if (!result?.decision) return;

        setSaving(true);
        try {
            const formData = new FormData();
            formData.set('journal_date', data.journal_date);
            formData.set('reference_number', data.reference_number || '');
            formData.set('from_account_id', data.from_account_id || 0);
            formData.set('to_account_id', data.to_account_id || 0);
            formData.set('notes', data.notes || '');
            formData.set('currency_id', data.currency_id || 0);
            formData.set('amount', data.amount || 0);
            formData.set('option', editData?.account_ref_no ? 2 : 1);
            if (editData?.account_ref_no) formData.set('account_ref_no', editData.account_ref_no);
            if (attachmentFile) formData.set('file', attachmentFile);
            else if (existingAttachmentUrl) formData.set('attachment_url', existingAttachmentUrl);

            const res = await bankingService.transferToAnotherAccount(formData);
            const { message_text: Message_text, message_id } = res.data[0] || res.data?.result_status?.[0];

            if (message_id === 1) {
                logicContext?.showToast?.(Message_text, "success");
                // Clear state immediately on success
                reset({
                    journal_date: moment().format('YYYY-MM-DD'),
                    reference_number: '',
                    from_account_id: accountId,
                    to_account_id: '',
                    notes: '',
                    currency_id: '',
                    amount: '',
                });
                setAttachmentFile(null);
                setExistingAttachmentUrl('');
                setFileKey(prev => prev + 1);
                onSaved?.();
                onClose();
            } else {
                logicContext?.showToast?.(Message_text, "error");
            }
        } catch (err) {
            console.error('Failed to transfer', err);
            logicContext?.showToast?.("Failed to submit transfer", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className={`fixed top-0 right-0 h-screen w-[480px] bg-white z-50 flex flex-col overflow-hidden
        transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
            style={{ borderLeft: '1px solid #e5e7eb', boxShadow: '-4px 0 24px rgba(0,0,0,0.10)' }}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                <div className="flex items-center gap-2">
                    <button
                        onClick={onBack}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition"
                    >
                        <ChevronLeft />
                    </button>
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest leading-none mb-0.5">{editData?.account_ref_no ? 'Edit' : 'New'} Transaction</p>
                        <h3 className="text-base font-bold text-gray-800 leading-tight">
                            {selectedType?.transaction_name || 'Transfer To Another Account'}
                        </h3>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition"
                >
                    <XIcon />
                </button>
            </div>

            {/* Bank context pill */}
            {bankName && (
                <div className="px-5 pt-4 pb-1 flex-shrink-0">
                    <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                        <span className="text-xs text-blue-700 font-medium">
                            From: <span className="font-bold">{bankName}</span>
                        </span>
                    </div>
                </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

                {/* Date | Reference Number */}
                <div className="grid grid-cols-2 gap-4">
                    <FormField label="Date" required error={errors.journal_date?.message}>
                        <input
                            type="date"
                            className={inputCls}
                            {...register('journal_date', { required: 'Date is required' })}
                        />
                    </FormField>

                    <FormField label="Reference #">
                        <input
                            type="text"
                            placeholder="e.g. REF-0042"
                            className={inputCls}
                            {...register('reference_number')}
                        />
                    </FormField>
                </div>

                {/* From Account | To Account */}
                <div className="grid grid-cols-2 gap-4">
                    <FormField label="From Account" required error={errors.from_account_id?.message}>
                        <div className="relative">
                            <select
                                className={selectCls}
                                {...register('from_account_id', { required: 'From account is required' })}
                            >
                                <option value="">Select account</option>
                                {groupedAccountOptions.map(group => (
                                    <optgroup key={group.label} label={group.label}>
                                        {group.accounts.map(acc => (
                                            <option key={acc.account_id} value={acc.account_id}>
                                                {acc.account_name}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                    </FormField>

                    <FormField label="To Account" required error={errors.to_account_id?.message}>
                        <div className="relative">
                            <select
                                className={selectCls}
                                {...register('to_account_id', { required: 'To account is required' })}
                            >
                                <option value="">Select account</option>
                                {groupedAccountOptions.map(group => (
                                    <optgroup key={group.label} label={group.label}>
                                        {group.accounts.map(acc => (
                                            <option key={acc.account_id} value={acc.account_id}>
                                                {acc.account_name}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                    </FormField>
                </div>

                {/* Currency | Amount */}
                <div className="grid grid-cols-2 gap-4">
                    <FormField label="Currency" required error={errors.currency_id?.message}>
                        <select
                            className={selectCls}
                            {...register('currency_id', { required: 'Currency is required' })}
                        >
                            <option value="">Select currency</option>
                            {currencyOptions.length > 0
                                ? currencyOptions.map(c => (
                                    <option key={c.currency_id} value={c.currency_id}>
                                        {c.currency_name || c.currency_code}
                                    </option>
                                ))
                                : <option value="1">NGN - Nigerian Naira</option>
                            }
                        </select>
                    </FormField>

                    <FormField label="Amount" required error={errors.amount?.message}>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            className={inputCls}
                            {...register('amount', { required: 'Amount is required', min: { value: 0.01, message: 'Amount must be greater than 0' } })}
                        />
                    </FormField>
                </div>

                {/* Notes */}
                <FormField label="Notes">
                    <textarea
                        rows={3}
                        maxLength={500}
                        placeholder="Optional notes…"
                        className={`${inputCls} resize-none`}
                        {...register('notes')}
                    />
                </FormField>

                {/* Attachment */}
                <FormField label="Attachment">
                    {existingAttachmentUrl && (
                        <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg">
                            <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            <a
                                href={existingAttachmentUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="flex-1 text-xs text-blue-600 underline truncate"
                            >
                                {existingAttachmentUrl.split('/').pop()}
                            </a>
                            <button
                                type="button"
                                onClick={() => setExistingAttachmentUrl('')}
                                className="w-5 h-5 flex items-center justify-center rounded-full bg-red-100 text-red-500 hover:bg-red-200 transition flex-shrink-0"
                                title="Remove attachment"
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    )}
                    <GeneralFileUploadPreview key={fileKey} onFileSelected={onFileSelected} />
                    <p className="text-xs text-gray-400 mt-1">Maximum file size 2MB</p>
                </FormField>

                <div className="h-2" />
            </form>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex gap-3 flex-shrink-0">
                <button
                    type="button"
                    onClick={handleSubmit(onSubmit)}
                    disabled={saving}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold py-2.5 px-4 rounded-lg transition shadow-sm"
                >
                    {saving ? 'Saving…' : editData?.account_ref_no ? 'Update' : 'Save & Publish'}
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 bg-white hover:bg-gray-100 text-gray-700 text-sm font-semibold py-2.5 px-4 rounded-lg border border-gray-200 transition"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};
